<!-- Polaris (fase 5.1): the page of the contacts list, one column in the frame under the Topbar: the head
     (PageHeader shape), the list with its own scroll (the table's header row sticks to it, no double scroll) and the
     kit's PaginationFooter (fase P) at the foot. `@container/list` lets the table drop columns by the room the list
     has, not the window (a drawer beside it takes room). Search keeps the woot's "load more". The `bulk` slot sits between the head and the list (5.2);
     the `drawer` slot is the column beside the list (the "New contact" drawer, 5.2; the contact, 5.3). -->
<script setup>
import { computed, ref } from 'vue';
import { useRoute } from 'vue-router';

import ContactListHeaderWrapper from 'dashboard/components-next/Contacts/ContactsHeader/ContactListHeaderWrapper.vue';
import ContactsActiveFiltersPreview from 'dashboard/components-next/Contacts/ContactsHeader/components/ContactsActiveFiltersPreview.vue';
import PaginationFooter from 'dashboard/components-next/polaris/cards/PaginationFooter.vue';
import ContactsLoadMore from 'dashboard/components-next/Contacts/ContactsLoadMore.vue';

const props = defineProps({
  searchValue: { type: String, default: '' },
  headerTitle: { type: String, default: '' },
  showPaginationFooter: { type: Boolean, default: true },
  currentPage: { type: Number, default: 1 },
  totalItems: { type: Number, default: 100 },
  itemsPerPage: { type: Number, default: 15 },
  activeSort: { type: String, default: '' },
  activeOrdering: { type: String, default: '' },
  activeSegment: { type: Object, default: null },
  segmentsId: { type: [String, Number], default: 0 },
  hasAppliedFilters: { type: Boolean, default: false },
  isFetchingList: { type: Boolean, default: false },
  useInfiniteScroll: { type: Boolean, default: false },
  hasMore: { type: Boolean, default: false },
  isLoadingMore: { type: Boolean, default: false },
});

const emit = defineEmits([
  'update:currentPage',
  'update:sort',
  'search',
  'applyFilter',
  'clearFilters',
  'loadMore',
  'add',
]);

const route = useRoute();

const contactListHeaderWrapper = ref(null);

const isNotSegmentView = computed(() => {
  return !route.name?.includes('segment');
});

const isActiveView = computed(() =>
  ['contacts_dashboard_active', 'contacts_edit_active'].includes(route.name)
);

const isLabelView = computed(() => !!route.name?.includes('label'));

const showActiveFiltersPreview = computed(() => {
  return (
    (props.hasAppliedFilters || !isNotSegmentView.value) &&
    !props.isFetchingList &&
    !isLabelView.value &&
    !isActiveView.value
  );
});

const updateCurrentPage = page => {
  emit('update:currentPage', page);
};

const openFilter = () => {
  contactListHeaderWrapper.value?.onToggleFilters();
};

const showLoadMore = computed(() => {
  return props.useInfiniteScroll && props.hasMore;
});

const showPagination = computed(() => {
  return !props.useInfiniteScroll && props.showPaginationFooter;
});

// The count beside the title: the view's total, once the list has it.
const headerCount = computed(() =>
  props.isFetchingList ? null : props.totalItems || null
);
</script>

<template>
  <section
    class="flex h-full min-h-0 w-full bg-polaris-background text-polaris-foreground"
  >
    <div class="flex min-w-0 flex-1 flex-col">
      <ContactListHeaderWrapper
        ref="contactListHeaderWrapper"
        :show-search="isNotSegmentView && !isActiveView"
        :search-value="searchValue"
        :active-sort="activeSort"
        :active-ordering="activeOrdering"
        :header-title="headerTitle"
        :count="headerCount"
        :active-segment="activeSegment"
        :segments-id="segmentsId"
        :has-applied-filters="hasAppliedFilters"
        :is-label-view="isLabelView"
        :is-active-view="isActiveView"
        @update:sort="emit('update:sort', $event)"
        @search="emit('search', $event)"
        @apply-filter="emit('applyFilter', $event)"
        @clear-filters="emit('clearFilters')"
        @add="emit('add')"
      />
      <slot name="bulk" />
      <main class="@container/list min-h-0 flex-1 overflow-auto">
        <ContactsActiveFiltersPreview
          v-if="showActiveFiltersPreview"
          :active-segment="activeSegment"
          class="px-6 pt-3"
          @clear-filters="emit('clearFilters')"
          @open-filter="openFilter"
        />
        <slot name="default" />
        <ContactsLoadMore
          v-if="showLoadMore"
          :is-loading="isLoadingMore"
          @load-more="emit('loadMore')"
        />
      </main>
      <footer
        v-if="showPagination"
        class="shrink-0 border-0 border-t border-solid border-polaris-border px-6 py-3"
      >
        <PaginationFooter
          :page="currentPage"
          :total="totalItems"
          :page-size="itemsPerPage"
          count-key="CONTACTS_POLARIS.PAGINATION.COUNT"
          showing-key="CONTACTS_POLARIS.PAGINATION.SHOWING"
          @update:page="updateCurrentPage"
        />
      </footer>
    </div>
    <slot name="drawer" />
  </section>
</template>
